/**
 * Formatting.
 *
 * Times are rendered in the room's own zone wherever a room is involved —
 * a person in Aqaba looking at an Amman room must see the Amman time.
 */

/**
 * Twelve- or twenty-four-hour, chosen once by the administrator and applied
 * everywhere a time is written: the portal, the admin pages, the tablet and
 * the status lines the server composes.
 *
 * Held here rather than passed down through every component, because a clock
 * format is a property of the customer, not of any one screen — and threading
 * it through thirty call sites is how half of them end up disagreeing.
 * `SessionProvider` sets it from the organisation as soon as it is known.
 */
let clock24Default = true;

export function setClockFormat(clock24: boolean): void {
  clock24Default = clock24;
}

export function clock24(): boolean {
  return clock24Default;
}

export function timeZoneOf(zone?: string | null): string {
  return zone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function formatTime(
  iso: string,
  zone?: string | null,
  hour24 = clock24Default,
): string {
  return formatClock(new Date(iso), zone, hour24);
}

/** The same, for a Date already in hand. */
export function formatClock(
  date: Date,
  zone?: string | null,
  hour24 = clock24Default,
): string {
  return date.toLocaleTimeString(hour24 ? "en-GB" : "en-US", {
    // Twelve-hour reads "2:20 PM", not "02:20 PM"; twenty-four-hour keeps the
    // leading zero so a column of times stays aligned.
    hour: hour24 ? "2-digit" : "numeric",
    minute: "2-digit",
    hour12: !hour24,
    timeZone: timeZoneOf(zone),
  });
}

export function formatDate(iso: string, zone?: string | null): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: timeZoneOf(zone),
  });
}

export function formatLongDate(iso: string, zone?: string | null): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: timeZoneOf(zone),
  });
}

export function formatDateTime(iso: string, zone?: string | null): string {
  return `${formatDate(iso, zone)}, ${formatTime(iso, zone)}`;
}

export function formatRange(startIso: string, endIso: string, zone?: string | null): string {
  return `${formatTime(startIso, zone)}–${formatTime(endIso, zone)}`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourLabel = hours === 1 ? "1 hour" : `${hours} hours`;
  return rest ? `${hourLabel} ${rest} min` : hourLabel;
}

export function durationBetween(startIso: string, endIso: string): number {
  return Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
}

export function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const delta = Date.now() - new Date(iso).getTime();
  const seconds = Math.round(delta / 1000);
  if (Math.abs(seconds) < 60) return seconds >= 0 ? "just now" : "in a moment";
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60)
    return minutes > 0 ? `${minutes} min ago` : `in ${-minutes} min`;
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return hours > 0 ? `${hours} h ago` : `in ${-hours} h`;
  const days = Math.round(hours / 24);
  return days > 0 ? `${days} d ago` : `in ${-days} d`;
}

export function percent(value: number, digits = 0): string {
  return `${(value * 100).toFixed(digits)}%`;
}

/** An ISO string for a datetime-local input, in the given zone. */
export function toLocalInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function fromLocalInput(value: string): Date {
  return new Date(value);
}

/** Round up to the next slot boundary — the default start time offered. */
export function nextSlot(step = 15, from: Date = new Date()): Date {
  const date = new Date(from);
  date.setSeconds(0, 0);
  const remainder = date.getMinutes() % step;
  date.setMinutes(date.getMinutes() + (remainder === 0 ? step : step - remainder));
  return date;
}

export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60000);
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

const SOURCE_LABELS: Record<string, string> = {
  portal: "Employee portal",
  qr: "Mobile QR",
  tablet: "Room tablet",
  admin: "Administrator",
  on_behalf: "Booked for someone",
  recurring: "Repeating series",
  ms365: "Microsoft 365",
  google: "Google Workspace",
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}

const STATUS_LABELS: Record<string, string> = {
  pending_approval: "Waiting for approval",
  confirmed: "Confirmed",
  checked_in: "Checked in",
  completed: "Completed",
  cancelled: "Cancelled",
  rejected: "Not approved",
  released: "Released early",
  no_show: "No-show",
  expired: "Expired",
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export type Tone = "available" | "soon" | "occupied" | "offline" | "neutral" | "accent";

/** Booking status → the four status tokens. Never invents a fifth colour. */
export function statusTone(status: string): Tone {
  switch (status) {
    case "confirmed":
    case "completed":
      return "available";
    case "checked_in":
      return "occupied";
    case "pending_approval":
      return "soon";
    case "cancelled":
    case "rejected":
    case "no_show":
    case "expired":
      return "offline";
    case "released":
      return "neutral";
    default:
      return "neutral";
  }
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}
